import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { LexicalEditor, NodeKey } from 'lexical';
import {
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  KEY_DOWN_COMMAND,
  KEY_ESCAPE_COMMAND,
} from 'lexical';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useEditorChrome } from '../../core/context/EditorChromeContext';
import { EditorPortal } from '../../ui/EditorPortal';
import { panelClasses } from '../../ui/styles';
import { characterRect } from '../../utils/characterRect';
import { registerAtomicDeletion } from '../../utils/registerAtomicDeletion';
import { EDIT_DYNAMIC_DATA_COMMAND } from './DynamicDataChip';
import { useLexicalDynamicData } from './DynamicDataContext';
import { $createDynamicDataNode, $isDynamicDataNode } from './DynamicDataNode';
import { registerDynamicDataTracking } from './registerDynamicDataTracking';
import { registerDynamicDataTransform } from './registerDynamicDataTransform';

/** Typing this opens the picker (V1's `DynamicDataKey`), anywhere in the text. */
const TRIGGER = '/';

/** Inserting at a typed `/` (its text node and offset), or editing a clicked chip. */
type Menu = { kind: 'insert'; key: NodeKey; offset: number } | { kind: 'edit'; nodeKey: NodeKey; id: string };

/**
 * Dynamic data: `/` opens the host's picker at the caret; picking replaces the `/` with a `DynamicDataNode`.
 * - Clicking a chip reopens the picker for it; picking points the chip at the picked key.
 * - Also mounts turning saved tokens back into chips, whole-chip deletion and the added/removed reporting.
 */
export function DynamicDataPlugin() {
  const [editor] = useLexicalComposerContext();
  const data = useLexicalDynamicData();
  const { focused } = useEditorChrome();
  const [menu, setMenu] = useState<Menu | null>(null);
  const close = useCallback(() => setMenu(null), []);

  // The latest host data, for listeners registered once
  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => registerDynamicDataTransform(editor), [editor]);
  useEffect(() => registerAtomicDeletion(editor, $isDynamicDataNode), [editor]);
  useEffect(
    () =>
      registerDynamicDataTracking(editor, {
        onAdded: (key) => dataRef.current?.onAdded?.(key),
        onRemoved: (key) => dataRef.current?.onRemoved?.(key),
      }),
    [editor],
  );

  // Open once the `/` is in the document, so the keystroke doesn't follow focus into the picker's search
  useEffect(
    () =>
      editor.registerCommand(
        KEY_DOWN_COMMAND,
        (event) => {
          const host = dataRef.current;
          if (event.key !== TRIGGER || !editor.isEditable() || !host?.renderPicker || host.readOnly) return false;
          setTimeout(() => {
            editor.getEditorState().read(() => {
              const selection = $getSelection();
              if (!$isRangeSelection(selection) || !selection.isCollapsed()) return;
              const node = selection.anchor.getNode();
              const at = selection.anchor.offset - 1;
              if (!$isTextNode(node) || node.getTextContent()[at] !== TRIGGER) return;
              setMenu({ kind: 'insert', key: node.getKey(), offset: at });
            });
          }, 0);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor],
  );

  useEffect(
    () =>
      editor.registerCommand(
        EDIT_DYNAMIC_DATA_COMMAND,
        (nodeKey) => {
          const node = $getNodeByKey(nodeKey);
          if (!$isDynamicDataNode(node)) return false;
          setMenu({ kind: 'edit', nodeKey, id: node.getId() });
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor],
  );

  // Close once what it was opened for is gone: the caret leaves the `/`, or the edited chip is removed
  useEffect(() => {
    if (!menu) return;
    const unregisterUpdate = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        if (menu.kind === 'edit') {
          if (!$isDynamicDataNode($getNodeByKey(menu.nodeKey))) close();
          return;
        }
        const selection = $getSelection();
        // No selection is the picker's search having the keyboard: the caret is still where it was
        if (selection === null) return;
        const stillAtTrigger =
          $isRangeSelection(selection) && selection.isCollapsed() && selection.anchor.key === menu.key && selection.anchor.offset === menu.offset + 1;
        if (!stillAtTrigger) close();
      });
    });
    const unregisterEscape = editor.registerCommand(
      KEY_ESCAPE_COMMAND,
      () => {
        close();
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
    return () => {
      unregisterUpdate();
      unregisterEscape();
    };
  }, [editor, menu, close]);

  // Deactivating the editor (a click outside it, tabbing away) closes it too
  useEffect(() => {
    if (!focused) close();
  }, [focused, close]);

  const pick = useCallback(
    (key: string) => {
      if (!menu) return;
      close();
      editor.update(
        () => {
          if (menu.kind === 'edit') {
            const chip = $getNodeByKey(menu.nodeKey);
            if ($isDynamicDataNode(chip) && chip.getId() !== key) chip.setId(key);
            return;
          }
          const node = $getNodeByKey(menu.key);
          // The `/` may have gone in the meantime (e.g. a collaborator's edit)
          if (!$isTextNode(node) || node.getTextContent()[menu.offset] !== TRIGGER) return;
          const chip = $createDynamicDataNode(key);
          node.select(menu.offset, menu.offset + 1).insertNodes([chip]);
          // Caret after the chip, so typing carries on behind it
          chip.selectNext();
        },
        // Focused once committed: `editor.focus()` skips the DOM focus while an update is pending
        { onUpdate: () => editor.focus() },
      );
    },
    [editor, menu, close],
  );

  const cancel = useCallback(() => {
    close();
    editor.focus();
  }, [editor, close]);

  if (!menu || !data?.renderPicker || data.readOnly) return null;
  const rect = menu.kind === 'insert' ? () => characterRect(editor, menu.key, menu.offset) : () => chipRect(editor, menu.nodeKey);
  return (
    <DynamicDataPopover editor={editor} rect={rect}>
      {data.renderPicker({ editing: menu.kind === 'edit' ? menu.id : undefined, onPick: pick, onClose: cancel })}
    </DynamicDataPopover>
  );
}

const chipRect = (editor: LexicalEditor, nodeKey: NodeKey): DOMRect => editor.getElementByKey(nodeKey)?.getBoundingClientRect() ?? new DOMRect();

type PopoverProps = {
  editor: LexicalEditor;
  rect: () => DOMRect;
  children: ReactNode;
};

/** The picker, placed below the `/` (or chip) and following it as the page scrolls or the text reflows. */
const DynamicDataPopover = ({ editor, rect, children }: PopoverProps) => {
  const { refs, floatingStyles, update } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    // top/left, not a transform: a transform would trap the picker's own fixed dropdowns inside it
    transform: false,
    middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  // Measured on every call, through node keys, since Lexical can replace the elements
  const rectRef = useRef(rect);
  rectRef.current = rect;
  const reference = useMemo(
    () => ({ getBoundingClientRect: () => rectRef.current(), contextElement: editor.getRootElement() ?? undefined }),
    [editor],
  );
  useLayoutEffect(() => refs.setPositionReference(reference), [refs, reference]);
  // Edits move the `/` or chip without a scroll or resize
  useEffect(() => editor.registerUpdateListener(() => update()), [editor, update]);

  return (
    // Not `keepSelection`: the picker's search needs the mouse-down to take focus
    <EditorPortal keepSelection={false} ref={refs.setFloating} style={floatingStyles} className={panelClasses}>
      {children}
    </EditorPortal>
  );
};
